import { Alert, Form, Input, Row } from "antd"
import { SystemDescriptions } from "src/common/descriptions"
import { getInputRequiredRule, stringToBase64 } from "src/common/util"
import { Col24 } from "src/components/Columns"
import { FormActionButtons } from "src/components/FormActionButtons"
import { UpdatePasswordRequest, UserError, UserErrorOrigin } from "../../state/types"
import { User } from "src/common/models/user"
import { useSelector } from "react-redux"
import { RootState } from "src/state/reducer"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { UPDATE_USER_PASSWORD_API } from "../../state/actionTypes"

interface Props {
    onFinish: (request: UpdatePasswordRequest) => void
    onCancel: VoidFunction
    user: User
}

interface ReduxProps {
    error?: UserError
    isUpdatingPassword: boolean
}

export const PasswordResetForm = (props: Props) => {
    const [form] = Form.useForm();

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.user.error,
        isUpdatingPassword: serviceLoading(state, [UPDATE_USER_PASSWORD_API]),
    }))

    const descriptions = SystemDescriptions.PAGES.USER.CREATE
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const onFinish = async (formValues) => {
        const request: UpdatePasswordRequest = {
            processType: "CHANGE",
            userIdProcess: props.user.id!,
            newPassword: stringToBase64(formValues.password),
            oldPassword: stringToBase64(formValues.oldPassword),
        }

        props.onFinish(request)
    }

    const getButtonStatus = (): boolean => {
        return !form.isFieldsTouched(true) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0
    }

    const handleCancelClick = () => {
        props.onCancel()
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CANCEL_BUTTON}
            actionText={descriptions.SAVE_BUTTON}
            actionDisabled={getButtonStatus}
            onCancel={handleCancelClick}
            actionLoading={reduxProps.isUpdatingPassword}
        />
    )

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col24>
                    <Form.Item
                        label={descriptions.FORM.OLD_PASSWORD.LABEL}
                        name={"oldPassword"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.OLD_PASSWORD.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.OLD_PASSWORD.PLACEHOLDER}
                            type="password"
                        />
                    </Form.Item>
                </Col24>
                <Col24>
                    <Form.Item
                        label={descriptions.FORM.PASSWORD.LABEL}
                        name={"password"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.PASSWORD.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.PASSWORD.PLACEHOLDER}
                            type="password"
                        />
                    </Form.Item>
                </Col24>
                <Col24>
                    <Form.Item
                        label={descriptions.FORM.PASSWORD_CONFIRM.LABEL}
                        name={"confirmPassword"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.PASSWORD_CONFIRM.LABEL),
                            {
                                validator: (_, value) => {
                                    const { password } = form.getFieldsValue()

                                    if (password !== value) {
                                        return Promise.reject(descriptions.ERROR_PASSWORDS_MISSMATCH)
                                    }

                                    return Promise.resolve()
                                }
                            },
                        ]}
                        validateTrigger={["onBlur", "onChange"]}
                    >
                        <Input
                            placeholder={descriptions.FORM.PASSWORD_CONFIRM.PLACEHOLDER}
                            type="password"
                        />
                    </Form.Item>
                </Col24>
            </Row>
            {
                reduxProps.error && reduxProps.error.type === UserErrorOrigin.UPDATE_USER_PASSWORD &&
                <Alert
                    message={"Error"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {renderButtons()}
        </Form>
    )

    return (
        renderForm()
    )
}
